// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import { EyeCheckIcon, EyeErrorIcon, EyeIcon } from "../../../assets/icons";

// Utilities
import {
  sectionCompletionState,
  useExamStatus,
} from "../../../hooks/useExamStatus";
import {
  getAllExamSections,
  isBinocular,
  isMonocularMulitpleSections,
} from "../../../utils/examReportsHelper";
import { eyeTypes } from "../../../enums";
import useClinic from "../../../hooks/useClinic";

const ExamCardShared = (row, devicesWithExams) => {
  // clinic context
  const { clinicLicensing } = useClinic();
  const canUseSynoptophoreExams = clinicLicensing?.canUseSynoptophoreExams;
  const canUseRAPDExams = clinicLicensing?.canUseRAPDExams;
  const canUseExamPackaging = clinicLicensing?.canUseExamPackaging;

  // internationalization
  const { t } = useTranslation();

  const { getExamStatus } = useExamStatus();

  const { eyeLeft, eyeRight } = eyeTypes;

  // exam status
  const examStatus = useMemo(() => {
    return getExamStatus(row, devicesWithExams);
  }, [devicesWithExams, getExamStatus, row]);

  const device = useMemo(
    () =>
      devicesWithExams?.find(
        (item) => item?.associatedExam?.exam?.id === row?.id
      ),
    [devicesWithExams, row]
  );
  const hasDevice = useMemo(
    () => device !== null && device !== undefined,
    [device]
  );
  const exam = useMemo(
    () => device?.associatedExam?.exam ?? row,
    [device, row]
  );

  // handles the checkmarks in table
  const testCompletionIcon = useMemo(() => {
    // don't show eye text for multi-exam packages
    const isMultiExamPackage = exam?.sectionTypeOrder?.length > 1;
    if (isMultiExamPackage) {
      return <></>;
    }

    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );
    const currentExamSectionsKey = examSectionsKeys?.filter(
      (key) => exam[key].length > 0
    );
    // 2
    if (isBinocular(exam)) {
      const allExamSections = getAllExamSections(exam);

      const isTerminated = allExamSections?.some(
        (section) =>
          section.completionState === sectionCompletionState.Terminated
      );
      const isDone = allExamSections?.[0]?.completionDate;

      return (
        <div className="flex flex-col capitalize ">
          <div
            className="flex"
            style={{
              opacity: isDone || isTerminated ? 1 : 0.5,
              color: isTerminated ? "#FF4A4A" : isDone ? "#22BE39" : "inherit",
            }}
          >
            {t("word_binocular")}
          </div>
        </div>
      );
    }
    if (isMonocularMulitpleSections(exam)) {
      const leftSection = exam[currentExamSectionsKey[0]].find(
        (section) => section?.eye === eyeLeft
      );
      const rightSection = exam[currentExamSectionsKey[0]].find(
        (section) => section?.eye === eyeRight
      );

      const leftTerminated =
        leftSection?.completionState &&
        leftSection?.completionState === sectionCompletionState.Terminated;
      const leftDone =
        leftSection?.completionState &&
        leftSection?.completionState !== sectionCompletionState.Undefined;

      const rightTerminated =
        rightSection?.completionState &&
        rightSection?.completionState === sectionCompletionState.Terminated;
      const rightDone =
        rightSection?.completionState &&
        rightSection?.completionState !== sectionCompletionState.Undefined;

      return (
        <div className="flex flex-col capitalize ">
          <div className="flex">
            {leftTerminated ? (
              <EyeErrorIcon
                style={{ opacity: 1, color: "#FF4A4A" }}
                data-testid="monocular-left-eye-icon"
              />
            ) : leftDone ? (
              <EyeCheckIcon
                style={{ opacity: 1, color: "#22BE39" }}
                data-testid="monocular-left-eye-icon"
              />
            ) : (
              <EyeIcon
                style={{ opacity: 0.5 }}
                data-testid="monocular-left-eye-icon"
              />
            )}

            {rightTerminated ? (
              <EyeErrorIcon
                style={{ marginLeft: "30px", opacity: 1, color: "#FF4A4A" }}
                data-testid="monocular-right-eye-icon"
              />
            ) : rightDone ? (
              <EyeCheckIcon
                style={{ marginLeft: "30px", opacity: 1, color: "#22BE39" }}
                data-testid="monocular-right-eye-icon"
              />
            ) : (
              <EyeIcon
                style={{ marginLeft: "30px", opacity: 0.5 }}
                data-testid="monocular-right-eye-icon"
              />
            )}
          </div>
          <div className="flex">
            <span
              style={{
                color: leftTerminated
                  ? "#FF4A4A"
                  : leftDone
                    ? "#22BE39"
                    : "inherit",
              }}
            >
              {t("word_left")}
            </span>
            <span style={{ marginLeft: "6px", marginRight: "6px" }}>/</span>
            <span
              style={{
                color: rightTerminated
                  ? "#FF4A4A"
                  : rightDone
                    ? "#22BE39"
                    : "inherit",
              }}
            >
              {t("word_right")}
            </span>
          </div>
        </div>
      );
    } else if (exam?.[currentExamSectionsKey[0]]?.length === 1) {
      const isLeftEye = exam[currentExamSectionsKey[0]][0]?.eye === eyeLeft;

      const isTerminated =
        exam[currentExamSectionsKey[0]][0]?.completionState ===
        sectionCompletionState.Terminated;
      const isDone =
        exam[currentExamSectionsKey[0]][0]?.completionState !==
        sectionCompletionState.Undefined;

      return (
        <div className="flex flex-col capitalize items-start">
          {isTerminated ? (
            <EyeErrorIcon
              style={{ opacity: 1, color: "#FF4A4A" }}
              data-testid="monocular-one-section-icon"
            />
          ) : isDone ? (
            <EyeCheckIcon
              style={{ opacity: 1, color: "#22BE39" }}
              data-testid="monocular-one-section-icon"
            />
          ) : (
            <EyeIcon
              style={{ opacity: 0.5 }}
              data-testid="monocular-one-section-icon"
            />
          )}
          <span
            style={{
              color: isTerminated ? "#FF4A4A" : isDone ? "#22BE39" : "inherit",
            }}
          >
            {isLeftEye ? t("word_left") : t("word_right")}
          </span>
        </div>
      );
    }
  }, [exam, t, eyeLeft, eyeRight]);

  const checkReportDisabled = useMemo(() => {
    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );
    const completedExamSections = examSectionsKeys?.filter(
      (key) =>
        exam[key]?.length > 0 &&
        exam[key]?.some((examSection) => Boolean(examSection.completionDate))
    );

    return completedExamSections?.length === 0;
  }, [exam]);

  // should disable the button in case the exam is completed
  const checkLinkDeviceDisabled = () =>
    examStatus?.status === t("word_completed") ||
    examStatus?.status === t("word_terminated") ||
    examStatus?.status === t("word_invalid");

  // define device tooltip message
  const checkTooltipDeviceMessage = () => {
    let reason = "";
    if (examStatus?.status === t("word_completed")) {
      reason = t("exams_link_device_tooltip_disabled_completed");
    } else if (
      examStatus?.status === t("word_terminated") &&
      !examStatus?.timelineStage
    ) {
      reason = t("exams_link_device_tooltip_disabled_terminated");
    } else if (hasDevice) {
      reason = t("exams_link_device_tooltip_disabled_linked");
    } else {
      reason = t("word_link_device");
    }
    return reason;
  };

  // check if live update modal button should be disabled
  const checkLiveUpdateDisabled = () =>
    !device?.deviceStatus?.stage?.stageType || !device?.associatedExam?.exam;

  // check if edit button should be disabled
  const isEditDisabled = useMemo(() => {
    const examSectionsKeys = Object.keys(exam).filter((key) =>
      key.includes("Sections")
    );
    const completedExamSections = examSectionsKeys?.filter(
      (key) =>
        exam[key]?.length > 0 &&
        exam[key]?.some((examSection) => Boolean(examSection.completionDate))
    );

    const isMultiPackageDisabled =
      !canUseExamPackaging && exam?.sectionTypeOrder?.length > 1;
    const isSynoptophoreDisabled =
      !canUseSynoptophoreExams && exam?.synoptophoreSections?.length > 0;
    const isRAPDDisabled = !canUseRAPDExams && exam?.rapdSections?.length > 0;
    const isPartiallyCompleteExam = completedExamSections?.length > 0;
    const isInvalidStatus = examStatus?.status !== t("word_not_started");

    return (
      isMultiPackageDisabled ||
      isSynoptophoreDisabled ||
      isRAPDDisabled ||
      isPartiallyCompleteExam ||
      isInvalidStatus ||
      hasDevice
    );
  }, [
    exam,
    canUseExamPackaging,
    canUseRAPDExams,
    canUseSynoptophoreExams,
    examStatus?.status,
    hasDevice,
    t,
  ]);

  return {
    exam,
    device,
    examStatus,
    hasDevice,
    testCompletionIcon,
    isEditDisabled,
    checkReportDisabled,
    checkLinkDeviceDisabled,
    checkTooltipDeviceMessage,
    checkLiveUpdateDisabled,
  };
};

export default ExamCardShared;
